import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { datasheetsApi, useInvalidateDatasheet, type SizeSlot } from "@/lib/datasheets";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasheetId: number;
  sizes: SizeSlot[];
}

const ZERO = [0, 0, 0, 0, 0, 0, 0];

export function RecordPurchaseDialog({ open, onOpenChange, datasheetId, sizes }: Props) {
  const invalidate = useInvalidateDatasheet();
  const named = useMemo(() => sizes.filter((s) => s.desc), [sizes]);

  const [quantities, setQuantities] = useState<number[]>(ZERO);
  const [delivery, setDelivery] = useState(0);
  const [fitting, setFitting] = useState(0);
  const [po, setPo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setQty = (index: number, value: number) =>
    setQuantities((prev) => prev.map((q, i) => (i === index - 1 ? value : q)));
  const hasSomething = quantities.some((q) => q > 0) || delivery > 0 || fitting > 0;

  async function submit() {
    setSubmitting(true);
    try {
      await datasheetsApi.addPurchase(datasheetId, {
        purchaseDate: new Date().toISOString(),
        sizeQuantities: quantities,
        deliveryChargeQuantity: delivery,
        prepaidFittingQuantity: fitting,
        delAmountPaid: null,
        quantityAmountPaid: null,
        invoiceId: null,
        clientPurchaseOrderNo: po || null,
        isAdjustment: false,
        adjustmentDesc: null,
        note: null,
      });
      invalidate(datasheetId);
      toast.success("Purchase recorded");
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 400 ? "Invalid — check the quantities." : "Could not record the purchase");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Record purchase</DialogTitle></DialogHeader>

        <div className="space-y-3">
          {named.map((s) => (
            <div key={s.index} className="flex items-center gap-3">
              <Label htmlFor={`p-size-${s.index}`} className="w-32">{s.desc}</Label>
              <Input
                id={`p-size-${s.index}`}
                aria-label={s.desc!}
                type="number"
                min={0}
                value={quantities[s.index - 1] || ""}
                onChange={(e) => setQty(s.index, Number(e.target.value) || 0)}
              />
            </div>
          ))}

          <div className="flex items-center gap-3">
            <Label htmlFor="p-delivery" className="w-32">Delivery charges</Label>
            <Input id="p-delivery" type="number" min={0} value={delivery || ""}
              onChange={(e) => setDelivery(Number(e.target.value) || 0)} />
          </div>

          <div className="flex items-center gap-3">
            <Label htmlFor="p-fitting" className="w-32">Fittings</Label>
            <Input id="p-fitting" type="number" min={0} value={fitting || ""}
              onChange={(e) => setFitting(Number(e.target.value) || 0)} />
          </div>

          <div className="flex items-center gap-3">
            <Label htmlFor="p-po" className="w-32">Customer PO</Label>
            <Input id="p-po" value={po} onChange={(e) => setPo(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={!hasSomething || submitting}>
            Record purchase
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
